export type QueryParamsLike = Record<string, any>;

const DEFAULT_IGNORE_KEYS = new Set(['limit', 'offset', 'order', 'category']);
const FLAG_KEYS = new Set(['suggested', 'bundles']);

function normalizeParamToList(val: any): string[] {
    if (val === null || val === undefined) return [];
    if (Array.isArray(val)) return val.map(String).map((s) => s.trim()).filter(Boolean);
    if (typeof val === 'string') {
        return val
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
    }
    return [String(val).trim()].filter(Boolean);
}

function isTruthyFlag(val: any): boolean {
    const s = (val ?? '').toString().toLowerCase().trim();
    return s === 'true' || s === '1';
}

/**
 * Returns true when current query params represent selecting ONLY this quick action.
 * - Ignores paging/sort keys (limit/offset/order/category)
 * - Rejects any other filter keys that are not in quickAction.queryObject
 * - Requires a single value match for non-flag keys
 * - Supports flag keys: suggested/bundles
 */
function isSingleQuickActionSelected(params: QueryParamsLike, quickAction: any, ignoreKeys: Set<string> = DEFAULT_IGNORE_KEYS): boolean {
    const q = quickAction?.queryObject || {};
    const paramKeys = Object.keys(params || {}).filter((k) => !ignoreKeys.has(k));

    // must not include other filter keys besides the quick-action itself
    for (const k of paramKeys) {
        const vList = normalizeParamToList(params[k]);
        if (vList.length === 0) continue;
        if (!(k in q)) return false;
    }

    // each quick action key must match and be "single" (no extra values)
    for (const k of Object.keys(q)) {
        const expected = normalizeParamToList(q[k]);

        if (FLAG_KEYS.has(k)) {
            if (!isTruthyFlag(params?.[k])) return false;
            continue;
        }

        const actual = normalizeParamToList(params?.[k]);
        if (expected.length !== 1) return false;
        if (actual.length !== 1) return false;
        if (actual[0] !== expected[0]) return false;
    }

    // ensure at least one quick-action key is actually present/active
    return Object.keys(q).some((k) => {
        if (FLAG_KEYS.has(k)) return isTruthyFlag(params?.[k]);
        return normalizeParamToList(params?.[k]).length > 0;
    });
}

export function findSingleQuickActionIndex(params: QueryParamsLike, quickActions: any[], ignoreKeys: Set<string> = DEFAULT_IGNORE_KEYS): number | null {
    const matches: number[] = [];
    (quickActions || []).forEach((qa: any, idx: number) => {
        if (isSingleQuickActionSelected(params, qa, ignoreKeys)) matches.push(idx);
    });
    return matches.length === 1 ? matches[0] : null;
}

export function findSingleQuickAction<T = any>(params: QueryParamsLike, quickActions: T[], ignoreKeys: Set<string> = DEFAULT_IGNORE_KEYS): T | null {
    const idx = findSingleQuickActionIndex(params, quickActions as any[], ignoreKeys);
    return idx === null ? null : (quickActions[idx] ?? null);
}

